import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { departmentsService, type CreateDepartmentData } from '@/services/departments.service';

// Query key factory
export const departmentKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...departmentKeys.lists(), { filters }] as const,
  details: () => [...departmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentKeys.details(), id] as const,
};

// Get departments (paginated)
export function useDepartments(params?: {
  page?: number;
  limit?: number;
  name?: string;
  organizationId?: string;
}) {
  return useQuery({
    queryKey: departmentKeys.list(params),
    queryFn: () => departmentsService.getDepartments(params),
  });
}

// Get single department
export function useDepartment(id?: string) {
  return useQuery({
    queryKey: departmentKeys.detail(id!),
    queryFn: () => departmentsService.getDepartment(id!),
    enabled: !!id,
  });
}

// Create department
export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDepartmentData) => departmentsService.createDepartment(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
}

// Update department
export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDepartmentData> }) =>
      departmentsService.updateDepartment(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
}

// Delete department
export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => departmentsService.deleteDepartment(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    },
  });
}
